/**
 * The overlay layer: dialog, popover, tooltip. The one place in the app a
 * shadow exists (§12.1).
 *
 * Hand-rolled on <dialog> rather than pulling in Radix: it gives focus
 * trapping, light-dismiss and top-layer stacking for free, which was the
 * only reason to reach for a library.
 *
 * There is no floating popover here on purpose. The plan reaches for one to
 * show a check's `detail` payload, but that content is dense, variable in
 * height and often worth reading next to the row above it -- an inline
 * <Disclosure> is both simpler and easier to compare against the page image.
 * A popover would also have to escape the viewer panes' `overflow: hidden`,
 * which is machinery bought for no gain.
 */
import { X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '~/lib/cn.ts'
import { IconButton } from './primitives.tsx'

// ---------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------

export function Dialog({
  open, onClose, title, children, footer, width = 560, closeLabel = 'Close',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: number
  closeLabel?: string
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    else if (!open && el.open) el.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onCancel={(e) => { e.preventDefault(); onClose() }}
      onClick={(e) => { if (e.target === ref.current) onClose() }}
      className={cn(
        'shadow-popover border border-[var(--border)] bg-[var(--bg-overlay)] p-0 text-[var(--text)]',
        'backdrop:bg-black/35 open:animate-[fade_120ms_ease-out]',
        // `open:flex`, never a bare `flex`: the UA stylesheet hides a closed
        // <dialog> with `display: none`, and any author `display` at all
        // beats a UA rule -- so a plain `flex` here renders every dialog in
        // the app permanently open.
        'flex-col overflow-hidden open:flex',
        /*
         * Below `sm` a dialog is a full-screen sheet, not a centred card.
         * The raw model reply is long monospace JSON and the details sheet
         * is a list of controls -- both want the whole screen on a phone,
         * and a card floating in 16px of margin only wastes it.
         *
         * `max-sm:` rather than a `sm:` prefix on every desktop rule: the
         * card *is* the design, and the sheet is the exception to it.
         */
        'max-sm:inset-0 max-sm:m-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:w-screen max-sm:max-w-none max-sm:rounded-none',
        // The notch above and the home indicator below, on the one layout
        // that reaches both edges.
        'max-sm:pt-safe max-sm:pb-safe',
        'sm:m-auto sm:max-h-[85dvh] sm:w-[calc(100vw-2rem)] sm:max-w-[var(--dialog-w)]',
        'sm:rounded-[var(--radius-container)]',
      )}
      style={{ '--dialog-w': `${width}px` } as React.CSSProperties}
    >
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--border)] px-4 py-2.5">
        <h2 className="text-[length:var(--text-title)] font-medium">{title}</h2>
        <IconButton label={closeLabel} onClick={onClose}>
          <X size={18} />
        </IconButton>
      </div>
      <div className="min-h-0 flex-1 overflow-auto overscroll-contain px-4 py-3 sm:max-h-[65dvh] sm:flex-none">
        {children}
      </div>
      {footer && (
        <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--border)] px-4 py-2.5">
          {footer}
        </div>
      )}
    </dialog>
  )
}
